import {useEffect} from 'react';
import {useMutation, useQuery, useSubscription} from '@apollo/client/react';

import {graphql} from '../gqlApp';
import type {SupportFeedQuery} from '../gqlApp/graphql';
import {useDebouncedRefetch} from './activity';

/**
 * Обращения в поддержку (SKS-124).
 *
 * Устройство то же, что у чата (SKS-78, SKS-249): ленту открытого обращения держит
 * подписка с полным payload, а список и счётчик в меню — агрегаты, их подписка шлёт
 * лишь сигнал «в обращениях кабинета изменилось», по которому клиент перезапрашивает
 * готовые запросы. Отправка остаётся мутацией.
 */

export const GET_SUPPORT_TICKETS = graphql(`
	query SupportTickets($companyId: ID!) {
		supportTickets(companyId: $companyId) {
			id
			number
			party
			status
			awaitingParty
			subjectType
			subjectId
			subjectLabel
			connectionId
			originPath
			parentTicketId
			openedByName
			createdAt
			lastMessageAt
			lastMessagePreview
			lastMessageAuthorParty
			lastMessageHasAttachment
			unreadCount
		}
	}
`);

export const SUPPORT_ACTIVITY = graphql(`
	subscription SupportActivity($companyId: ID!) {
		supportActivity(companyId: $companyId) {
			companyId
		}
	}
`);

/**
 * Подписка на сигнал «в обращениях кабинета изменилось» → перезапрос указанных
 * запросов. refetchQueries по имени: перезапросятся только активные в кэше.
 *
 * Наружу — ради главного экрана (SKS-123): он показывает счётчик открытых обращений
 * и обязан просыпаться от того же сигнала, что список обращений. Обращения не пишутся
 * в журнал событий (см. комментарий к SupportModule), поэтому общий companyActivity
 * их не покрывает, и своей подпиской дашборд обойтись не может.
 */
export const useSupportActivityRefetch = (companyId: string | undefined, queries: string[]) => {
	const refetch = useDebouncedRefetch(queries);

	useSubscription(SUPPORT_ACTIVITY, {
		variables: {companyId: companyId ?? ''},
		skip: !companyId,
		onData: ({client}) => refetch(client),
	});
};

export const useSupportTickets = (companyId: string | undefined) => {
	useSupportActivityRefetch(companyId, ['SupportTickets']);

	return useQuery(GET_SUPPORT_TICKETS, {
		variables: {companyId: companyId ?? ''},
		skip: !companyId,
	});
};

export const GET_SUPPORT_TICKET = graphql(`
	query SupportTicket($ticketId: ID!) {
		supportTicket(ticketId: $ticketId) {
			id
			number
			companyId
			party
			status
			awaitingParty
			subjectType
			subjectId
			subjectLabel
			connectionId
			originPath
			parentTicketId
			openedByName
			createdAt
			unreadCount
		}
	}
`);

/**
 * Шапка обращения: номер, статус, предмет, «чьего ответа ждём».
 *
 * С перезапросом по сигналу активности, как список и счётчик: шапка меняется
 * не только от своих действий. Поддержка закрыла обращение — рядом с уже
 * приехавшей строкой «Обращение закрыто» оставалась метка «В работе» и кнопка
 * «Вопрос снят», то есть страница спорила сама с собой до перезагрузки.
 */
export const useSupportTicket = (ticketId: string | undefined, companyId?: string) => {
	useSupportActivityRefetch(companyId, ['SupportTicket']);

	return useQuery(GET_SUPPORT_TICKET, {
		variables: {ticketId: ticketId ?? ''},
		skip: !ticketId,
	});
};

export const GET_SUPPORT_FEED = graphql(`
	query SupportFeed($ticketId: ID!, $before: DateTime, $limit: Int) {
		supportFeed(ticketId: $ticketId, before: $before, limit: $limit) {
			hasMore
			items {
				id
				kind
				occurredAt
				authorParty
				authorName
				isMine
				body
				hasAttachment
				attachmentFileName
				attachmentMimeType
				attachmentSizeBytes
				statusTo
				reason
			}
		}
	}
`);

export const SUPPORT_MESSAGE_ADDED = graphql(`
	subscription SupportMessageAdded($ticketId: ID!, $companyId: ID) {
		supportMessageAdded(ticketId: $ticketId, companyId: $companyId) {
			id
			kind
			occurredAt
			authorParty
			authorName
			isMine
			body
			hasAttachment
			attachmentFileName
			attachmentMimeType
			attachmentSizeBytes
			statusTo
			reason
		}
	}
`);

/**
 * Лента обращения с досылкой новых сообщений подпиской.
 *
 * `companyId` в подписке говорит, с чьей стороны смотрим: кабинет подписывается
 * со своим, поддержка — без него. Сервер сверяет заявленное с действительным,
 * поэтому подставить чужой кабинет и получить чужую ленту нельзя.
 */
export const useSupportFeed = (ticketId: string | undefined, companyId: string | undefined) => {
	const result = useQuery(GET_SUPPORT_FEED, {
		variables: {ticketId: ticketId ?? ''},
		skip: !ticketId,
		// Страницы старше первой доливает fetchMore на странице обращения, и ответы
		// склеиваются там же вручную — своей политики слияния у ленты нет
	});

	const {subscribeToMore} = result;

	// Дедуп по id: отправитель получает своё сообщение и ответом мутации, и эхом
	// подписки, а на реконнекте страница ещё и перезапросится
	useEffect(() => {
		if (!ticketId) {
			return;
		}

		return subscribeToMore({
			document: SUPPORT_MESSAGE_ADDED,
			variables: {ticketId, companyId},
			updateQuery: (_prev, {previousData, subscriptionData}) => {
				const added = subscriptionData.data?.supportMessageAdded;
				// previousData типизирован deep-partial (кэш мог быть неполным), но при
				// cache-and-network это полная страница ленты — приводим к её типу
				const prev = previousData as SupportFeedQuery | undefined;

				if (!added || !prev?.supportFeed) {
					return;
				}

				if (prev.supportFeed.items.some((item) => item.id === added.id)) {
					return;
				}

				return {
					...prev,
					supportFeed: {...prev.supportFeed, items: [...prev.supportFeed.items, added]},
				};
			},
		});
	}, [ticketId, companyId, subscribeToMore]);

	return result;
};

export const GET_SUPPORT_UNREAD_TOTAL = graphql(`
	query SupportUnreadTotal($companyId: ID!) {
		supportUnreadTotal(companyId: $companyId)
	}
`);

/**
 * Счётчик непрочитанного по кабинету.
 *
 * `subscribe: false` — для второго места, которое показывает то же число (кнопка
 * в шапке): подписку держит пункт меню, а запрос в кэше один, и обновление
 * доезжает до обоих. Две подписки на один топик означали бы два соединения
 * и два таймера перезапроса.
 */
export const useSupportUnreadTotal = (
	companyId: string | undefined,
	options?: {subscribe?: boolean},
) => {
	useSupportActivityRefetch(options?.subscribe === false ? undefined : companyId, [
		'SupportUnreadTotal',
	]);

	return useQuery(GET_SUPPORT_UNREAD_TOTAL, {
		variables: {companyId: companyId ?? ''},
		skip: !companyId,
	});
};

const CREATE_SUPPORT_TICKET = graphql(`
	mutation CreateSupportTicket($input: CreateSupportTicketInput!) {
		createSupportTicket(input: $input) {
			id
			number
		}
	}
`);

export const useCreateSupportTicket = () =>
	useMutation(CREATE_SUPPORT_TICKET, {
		refetchQueries: ['SupportTickets', 'SupportUnreadTotal'],
		awaitRefetchQueries: true,
	});

const SEND_SUPPORT_MESSAGE = graphql(`
	mutation SendSupportMessage($input: SendSupportMessageInput!) {
		sendSupportMessage(input: $input) {
			id
			kind
			occurredAt
			authorParty
			authorName
			isMine
			body
			hasAttachment
			attachmentFileName
			attachmentMimeType
			attachmentSizeBytes
		}
	}
`);

/**
 * Refetch по именам, а не ручная правка кэша, — как везде в проекте: активные
 * запросы с текущими переменными перечитаются сами. Обращение здесь тоже:
 * сообщение в закрытое обращение открывает его снова, и шапка обязана это показать.
 */
export const useSendSupportMessage = () =>
	useMutation(SEND_SUPPORT_MESSAGE, {
		refetchQueries: ['SupportFeed', 'SupportTicket', 'SupportTickets'],
		awaitRefetchQueries: true,
	});

const MARK_SUPPORT_READ = graphql(`
	mutation MarkSupportRead($input: MarkSupportReadInput!) {
		markSupportRead(input: $input)
	}
`);

export const useMarkSupportRead = () =>
	useMutation(MARK_SUPPORT_READ, {
		refetchQueries: ['SupportUnreadTotal', 'SupportTickets'],
	});

const RESOLVE_SUPPORT_TICKET = graphql(`
	mutation ResolveSupportTicket($input: ResolveSupportTicketInput!) {
		resolveSupportTicket(input: $input) {
			id
			status
			awaitingParty
		}
	}
`);

export const useResolveSupportTicket = () =>
	useMutation(RESOLVE_SUPPORT_TICKET, {
		refetchQueries: ['SupportFeed', 'SupportTicket', 'SupportTickets'],
		awaitRefetchQueries: true,
	});
